import { kioskCommand } from 'api/Kiosk';
import { updateInvoices } from 'api/Invoice';
import { createExternalInvoices } from 'api/Billing';
import { createInstantPacket } from 'api/InstantPacket';

import { ia, iaRa, mString } from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';
import Typography from 'components/shared/Typography/Typography';

import { chargePayrocPaymentHandler, saveCardHandler } from './payrocMethods';
import {
  chargeElavonOnlineHandler,
  elavonPreAuthDeferredHandler,
  elavonSavedCardHandler
} from './elavonMethods';
import {
  appliedBalanceHandler,
  chargeCashHandler,
  chargeCheckHandler,
  chargeOutsideFinancingHandler,
  noPaymentSelectedHandler
} from './genericMethods';

const sendCommandToKiosk = async (
  navigate,
  {
    methodDetail,
    amount,
    invoices,
    appliedBalance,
    surchargeAmount,
    patientId,
    data,
    terminalId,
    saveCard,
    recurringPayment,
    planId,
    scheduleId,
    updatePlanId,
    ..._rest
  }
) => {
  if (!methodDetail) {
    showAlert({
      color: 'danger',
      title: 'No kiosk selected',
      message: 'Please select a kiosk to send the payment request to.'
    });
    return { code: 1 };
  }

  await updateInvoices(navigate, { invoices });
  const internalDetails = iaRa(invoices).map(({ id, balance }) => ({
    id,
    amount_paid: balance
  }));

  const selectedInternalInvoices = [
    {
      amount_cents: Math.round(amount) - (surchargeAmount || 0),
      total_amount_cents: Math.round(amount),
      due_date: new Date(),
      internal_invoice_ids: iaRa(invoices).map((i) => i.id),
      memo: '',
      patient: { id: patientId },
      tax_percentage: 0,
      surcharge_amount: surchargeAmount,
      aeob: false,
      is_detailed: false,
      type: 'kiosk',
      internal_details: internalDetails
    }
  ];

  const newExternalInvoice = await createExternalInvoices(navigate, {
    invoices: selectedInternalInvoices,
    skipConfirmation: true
  });

  if (!ia(newExternalInvoice?.invoices)) {
    showAlert({
      color: 'danger',
      title: "Couldn't send the request to the kiosk",
      message: 'Please try adding an invoice.'
    });
    return;
  }

  const extInvoice = newExternalInvoice?.invoices?.[0];

  if (!extInvoice?.link) {
    showAlert({
      color: 'danger',
      title: "Couldn't send the request to the kiosk",
      message: 'No eligible invoices were selected for payment. Please select a different invoice.'
    });
    return;
  }

  const isKioskPaymentFlow = !!(planId || updatePlanId || recurringPayment);
  const kioskUrl = isKioskPaymentFlow
    ? extInvoice.link.replace('/invoice/', '/kiosk/payment/')
    : extInvoice.link;

  const kioskCommandRes = await kioskCommand(navigate, {
    kioskId: methodDetail,
    command: 'url-change',
    url: kioskUrl,
    patientId: extInvoice?.patient_id,
    externalInvoiceToken: extInvoice?.token,
    page: 'Payment',
    data: {
      ...data,
      externalInvoiceToken: extInvoice?.token,
      terminalId,
      saveCard,
      ...(recurringPayment && { recurringPayment }),
      ...(planId && { planId }),
      ...(scheduleId && { scheduleId }),
      ...(updatePlanId && { updatePlanId })
    },
    appliedBalance
  });

  if (kioskCommandRes?.code !== 0) {
    throw new Error(kioskCommandRes);
  }

  showAlert({
    icon: 'outbound-fax-success',
    color: 'success',
    title: `Payment request sent`,
    message: (
      <Typography variant="p" size="small" className="block">
        Payment request sent to kiosk{' '}
        <span className="font-semibold">{kioskCommandRes?.kioskName}</span> for{' '}
        <span className="font-semibold">{mString(amount)}</span>
      </Typography>
    ),
    duration: 15000,
    variant: 'light',
    footerActions: {
      secondary: {
        text: 'Cancel payment',
        color: 'neutral',
        onClick: async () => {
          await kioskCommand(navigate, {
            kioskId: methodDetail,
            command: 'payment-cancel'
          });
          await kioskCommand(navigate, {
            kioskId: methodDetail,
            command: 'idle'
          });
          showAlert({
            icon: 'outbound-fax-error',
            color: 'success',
            title: 'Payment request canceled',
            message: 'Payment request canceled.'
          });
        }
      }
    }
  });

  return { code: 0, kioskName: kioskCommandRes?.kioskName };
};

const planExternalInvoiceHandler = async (
  navigate,
  { patientId, amount, planId, scheduleId, surchargeAmount, surchargeEnabled }
) => {
  const amountCents = Math.round(Number(amount || 0));
  if (!amountCents || amountCents <= 0) {
    showAlert({ color: 'danger', title: 'Enter an amount greater than zero' });
    return { code: 1 };
  }

  const effectiveSurcharge = surchargeEnabled ? Math.round(Number(surchargeAmount || 0)) : 0;

  const memo = scheduleId
    ? 'Payment plan installment'
    : planId
      ? 'Payment plan payoff'
      : 'Payment request';

  const payload = {
    invoices: [
      {
        amount_cents: amountCents,
        total_amount_cents: amountCents + effectiveSurcharge,
        memo,
        internal_invoice_ids: [],
        patient: { id: patientId },
        tax_percentage: 0,
        surcharge_amount: effectiveSurcharge,
        aeob: false,
        is_detailed: false,
        type: 'kiosk',
        internal_details: [],
        ...(planId && { payment_plan_id: planId }),
        ...(scheduleId && { payment_plan_schedule_id: scheduleId })
      }
    ],
    skipConfirmation: true
  };

  const result = await createExternalInvoices(navigate, payload);
  const externalInvoice = result?.invoices?.[0] || null;

  if (result?.code !== 0 || !externalInvoice) {
    showAlert({ color: 'danger', title: result?.error || "Couldn't create the invoice" });
    return { code: result?.code ?? 1 };
  }

  return {
    code: 0,
    externalInvoice,
    patient_id: patientId
  };
};

const requestPaymentHandler = async (
  navigate,
  {
    patientId,
    updatePlanId,
    recurringPayment,
    amount,
    appointmentId,
    invoices,
    surchargeAmount,
    planId,
    scheduleId,
    surchargeEnabled
  }
) => {
  const isPlanPayment = (!!scheduleId || !!planId) && !recurringPayment && !updatePlanId;

  if (isPlanPayment) {
    return planExternalInvoiceHandler(navigate, {
      patientId,
      amount,
      planId,
      scheduleId,
      surchargeAmount,
      surchargeEnabled
    });
  }

  const res = await createInstantPacket(navigate, {
    formIds: ['pre-auth'],
    patientId,
    ...(updatePlanId && { updatePlanId }),
    ...(recurringPayment && {
      recurringPayment,
      amount,
      appointmentId,
      invoices,
      surchargeAmount
    })
  });

  if (!res?.token) {
    showAlert({ color: 'danger', title: "Couldn't create the pre-authorization link" });
    return { code: 1 };
  }

  return {
    code: 0,
    instantPacketToken: res.token,
    patient_id: patientId,
    ...(res.planId && { planId: res.planId })
  };
};

const getUpfront = (props) => Number(props?.recurringPayment?.upfrontPayment) || 0;

export const paymentMethodApiElavon = {
  card: (navigate, props) =>
    sendCommandToKiosk(navigate, { type: 'card', data: { processor: 'elavon' }, ...props }),
  'saved-cards': elavonSavedCardHandler,
  'credit-card': chargeElavonOnlineHandler,
  'pre-auth': (navigate, props) =>
    chargeElavonOnlineHandler(navigate, { tokenOnly: true, ...props }),
  'pre-auth-deferred': elavonPreAuthDeferredHandler,
  'recurring-charge': (navigate, props) => {
    const upfront = getUpfront(props);
    if (upfront > 0) {
      return chargeElavonOnlineHandler(navigate, { ...props, amount: upfront });
    }
    return chargeElavonOnlineHandler(navigate, { tokenOnly: true, ...props });
  },
  'request-payment': requestPaymentHandler
};

export const paymentMethodApiPayroc = {
  card: (navigate, props) =>
    sendCommandToKiosk(navigate, { type: 'card', data: { processor: 'payroc' }, ...props }),
  'saved-cards': (navigate, props) =>
    chargePayrocPaymentHandler(navigate, { type: 'card', ...props }),
  ach: (navigate, props) => chargePayrocPaymentHandler(navigate, { type: 'ach', ...props }),
  'credit-card': (navigate, props) =>
    chargePayrocPaymentHandler(navigate, { type: 'card', ...props }),
  'pre-auth': (navigate, props) => saveCardHandler(navigate, { type: 'card', ...props }),
  'pre-auth-deferred': (_navigate, props) =>
    Promise.resolve({
      code: 0,
      token: props?.token,
      type: props?.type || 'card',
      terminalId: props?.terminalId,
      preAuthForm: props?.preAuthForm,
      deferred: true
    }),
  'recurring-charge': (navigate, props) => {
    const upfront = getUpfront(props);
    if (upfront > 0) {
      return chargePayrocPaymentHandler(navigate, { type: 'card', ...props, amount: upfront });
    }
    return saveCardHandler(navigate, { type: 'card', ...props });
  },
  vpayment: (navigate, props) => {
    return chargePayrocPaymentHandler(navigate, { type: 'card', ...props });
  }
};

export const genericMethodsApi = {
  cash: chargeCashHandler,
  check: chargeCheckHandler,
  'outside-financing': chargeOutsideFinancingHandler,
  'defer-payment': noPaymentSelectedHandler,
  'request-payment': requestPaymentHandler,
  'applied-balance': appliedBalanceHandler
};
